import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OwnershipLevel } from '@fitness-force/enum';
import { AppErrors, NotFoundError } from '@fitness-force/models';
import type { BadRequestError } from '@fitness-force/models';;
import type { PushNotificationTemplateMaster, PushNotificationTemplateMasterResponse, CreatePushNotificationTemplateRequest } from '@fitness-force/models';
import { environment } from '@fitness-force/environments';
import { throwError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationTemplateManagementService {
  private http = inject(HttpClient);
  private router = inject(Router);


  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  createPushNotificationTemplate(
    ownership: OwnershipLevel, 
    payload: CreatePushNotificationTemplateRequest, 
    tenantId: number, 
    franchiseGroupId: number
  ): Observable<any> {
    let url = "";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.CREATE_PUSH_TEMPLATE;
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.CREATE_PUSH_TEMPLATE
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.CREATE_PUSH_NOTIFICATION_TEMPLATE
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.CREATE_PUSH_NOTIFICATION_TEMPLATE
          .replace('{tenant_id}', tenantId.toString());
        break;
    }
    return this.http.post(url, payload, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updatePushNotificationTemplate(
    ownership: OwnershipLevel,
    tenantId: number,
    franchiseGroupId: number,
    push_notification_template_master_id: number,
    data: any
  ): Observable<any> {
    let url = "";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.UPDATE_PUSH_TEMPLATE
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.UPDATE_PUSH_TEMPLATE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_PUSH_NOTIFICATION_TEMPLATE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_PUSH_NOTIFICATION_TEMPLATE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
    }
    return this.http.put(url, data, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getPushNotificationTemplatesByOwnership(
    ownership: OwnershipLevel, 
    tenantId: number, 
    franchiseGroupId: number
  ): Observable<PushNotificationTemplateMasterResponse> {
    let url = "";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_PUSH_TEMPLATES;
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_PUSH_TEMPLATES
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_PUSH_NOTIFICATION_TEMPLATES
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_PUSH_NOTIFICATION_TEMPLATES
          .replace('{tenant_id}', tenantId.toString());
        break;
    }
    return this.http.get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getPushNotificationTemplateByIdWithOwnership(
    ownership: OwnershipLevel, 
    templateId: number, 
    franchiseGroupId: number, 
    tenantId: number
  ): Observable<PushNotificationTemplateMaster> {
    let url = "";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_PUSH_TEMPLATE_BY_ID
          .replace('{template_id}', templateId.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_PUSH_TEMPLATE_BY_ID
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', templateId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_PUSH_NOTIFICATION_TEMPLATE_BY_ID
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', templateId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_PUSH_NOTIFICATION_TEMPLATE_BY_ID
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', templateId.toString());
        break;
    }
    return this.http.get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  deletePushNotificationTemplate(
    ownership: OwnershipLevel, 
    tenantId: number, 
    franchiseGroupId: number, 
    push_notification_template_master_id: number
  ): Observable<any> {
    let url = "";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.UPDATE_PUSH_TEMPLATE
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.UPDATE_PUSH_TEMPLATE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.DELETE_PUSH_NOTIFICATION_TEMPLATE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.DELETE_PUSH_NOTIFICATION_TEMPLATE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
    }
    return this.http.delete(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActivePushNotificationTemplateByOwnership(
    ownership: OwnershipLevel, 
    tenantId: number, 
    franchiseGroupId: number, 
    push_notification_template_master_id: number
  ): Observable<any> {
    let url = "";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.MARK_PUSH_TEMPLATE_INACTIVE
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.MARK_PUSH_TEMPLATE_INACTIVE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_PUSH_NOTIFICATION_TEMPLATE_INACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_PUSH_NOTIFICATION_TEMPLATE_INACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
    }
    return this.http.post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markActivePushNotificationTemplateByOwnership(
    ownership: OwnershipLevel, 
    tenantId: number, 
    franchiseGroupId: number, 
    push_notification_template_master_id: number
  ): Observable<any> {
    let url = "";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.MARK_PUSH_TEMPLATE_ACTIVE
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.MARK_PUSH_TEMPLATE_ACTIVE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_PUSH_NOTIFICATION_TEMPLATE_ACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_PUSH_NOTIFICATION_TEMPLATE_ACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', push_notification_template_master_id.toString());
        break;
    }
    return this.http.post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
} 